import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar, Loader2 } from 'lucide-react';
import {
  getCalendarStatus,
  initiateCalendarOAuth,
  CalendarProvider,
  CalendarConnection,
  getMeetings,
  Meeting,
} from '../../services/Calendar';
import { MeetingSettingsModal } from './MeetingSettingsModal';
import { MeetingCard } from './MeetingCard';
import { GoogleCalendarIcon, OutlookCalendarIcon } from '../icons/CalendarIcons';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useSidebar } from '@/components/ui/sidebar';
import { useI18n } from '../../i18n';

interface MeetingsDrawerProps {
  open: boolean;
  onClose: () => void;
}

const POLLING_INTERVAL = 2000; // 2 seconds
const OAUTH_TIMEOUT = 60000; // 60 seconds

export const MeetingsDrawer: React.FC<MeetingsDrawerProps> = ({ open, onClose }) => {
  const { state: sidebarState } = useSidebar();
  const { t } = useI18n();
  const d = t.meetings.drawer;
  // Read inside fetchCalendarStatus so the open-drawer effect doesn't depend on the language
  const drawerTextRef = useRef(d);
  drawerTextRef.current = d;
  const [calendars, setCalendars] = useState<CalendarConnection[]>([]);
  const [meetingAssistantEnabled, setMeetingAssistantEnabled] = useState(false);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [meetingsLoading, setMeetingsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [connectingProvider, setConnectingProvider] = useState<CalendarProvider | null>(null);
  const [oauthPopup, setOauthPopup] = useState<Window | null>(null);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [oauthTimeout, setOauthTimeout] = useState<NodeJS.Timeout | null>(null);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  // Fetch calendar status when drawer opens
  useEffect(() => {
    if (open) {
      fetchCalendarStatus();
    }
  }, [open]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
      if (oauthTimeout) clearTimeout(oauthTimeout);
      if (oauthPopup && !oauthPopup.closed) oauthPopup.close();
    };
  }, [pollingInterval, oauthTimeout, oauthPopup]);

  const fetchCalendarStatus = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    const response = await getCalendarStatus();

    if (response.success && response.data) {
      setCalendars(response.data.calendars);
      setMeetingAssistantEnabled(response.data.meetingAssistantEnabled);
    } else {
      // Don't show error if it's just a 404 (backend not ready yet)
      if (response.status !== 404) {
        setError(response.errors?.[0]?.title || drawerTextRef.current.fetchStatusFailed);
      }
    }

    setLoading(false);
  };

  const fetchMeetings = useCallback(async () => {
    if (calendars.length === 0) return;
    
    setMeetingsLoading(true);
    
    // Fetch meetings for next 30 days and past 150 days
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 150);
    
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 30);
    
    const response = await getMeetings({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });
    
    if (response.success && response.data) {
      setMeetings(response.data);
    }
    
    setMeetingsLoading(false);
  }, [calendars.length]);

  // Fetch meetings when drawer opens and calendar is connected
  useEffect(() => {
    if (open && calendars.length > 0) {
      fetchMeetings();
    }
  }, [open, calendars.length, fetchMeetings]);

  const handleConnectCalendar = async (provider: CalendarProvider) => {
    setError(null);
    setSuccess(null);
    setConnectingProvider(provider);

    // Get OAuth URL from backend
    const response = await initiateCalendarOAuth(provider);

    if (!response.success || !response.data) {
      setError(response.errors?.[0]?.title || d.oauthFailed);
      setConnectingProvider(null);
      return;
    }

    const { oauth_url } = response.data;

    // Open OAuth popup
    const popup = window.open(
      oauth_url,
      'Calendar OAuth',
      'width=600,height=800,left=100,top=100'
    );

    if (!popup) {
      setError(d.popupBlocked);
      setConnectingProvider(null);
      return;
    }

    setOauthPopup(popup);

    // Start polling for connection status
    const interval = setInterval(async () => {
      const statusResponse = await getCalendarStatus();

      if (statusResponse.success && statusResponse.data) {
        const newCalendars = statusResponse.data.calendars;

        // Check if a new calendar was added
        if (newCalendars.length > calendars.length) {
          // Success! New calendar connected
          setCalendars(newCalendars);
          setSuccess(d.connected(provider === 'google_calendar' ? 'Google Calendar' : 'Microsoft Outlook'));
          setConnectingProvider(null);

          // Close popup and clear interval
          if (popup && !popup.closed) popup.close();
          clearInterval(interval);
          if (oauthTimeout) clearTimeout(oauthTimeout);
          setPollingInterval(null);
          setOauthTimeout(null);
          setOauthPopup(null);
        }
      }
    }, POLLING_INTERVAL);

    setPollingInterval(interval);

    // Set timeout to stop polling after 60 seconds
    const timeout = setTimeout(() => {
      if (popup && !popup.closed) popup.close();
      clearInterval(interval);
      setError(d.timedOut);
      setConnectingProvider(null);
      setPollingInterval(null);
      setOauthTimeout(null);
      setOauthPopup(null);
    }, OAUTH_TIMEOUT);

    setOauthTimeout(timeout);
  };

  const isProviderConnected = (provider: CalendarProvider): boolean => {
    return calendars.some(cal => cal.provider === provider);
  };

  const calendarConnected = calendars.length > 0;
  const sidebarWidth = sidebarState === 'collapsed' ? 'var(--sidebar-width-icon)' : 'var(--sidebar-width)';

  // Split meetings into upcoming and past
  const now = new Date();
  const upcomingMeetings = meetings
    .filter(m => new Date(m.startTime) >= now && m.botScheduled && !m.isCancelled)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  const pastMeetings = meetings
    .filter(m => new Date(m.startTime) < now && !m.isCancelled)
    .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

  return (
    <>
      <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }} modal={false}>
        <SheetContent 
          side="right"
          overlayStyle={{ left: sidebarWidth }}
          className="!w-[480px] !max-w-none p-0 flex flex-col !right-auto h-screen z-[100] !inset-y-0 data-[state=open]:animate-none data-[state=closed]:animate-none !duration-0 !transition-none"
          style={{ left: sidebarWidth }}
          onInteractOutside={(e) => {
            // Prevent closing when clicking on the sidebar
            const target = e.target as HTMLElement;
            const sidebar = target.closest('[data-sidebar="sidebar"]');
            if (sidebar) {
              e.preventDefault();
            }
          }}
        >
          <SheetHeader className="p-4 border-b flex-row items-center space-y-0">
            <div className="flex items-center gap-2 flex-1">
              <Calendar className="h-5 w-5" />
              <SheetTitle>{d.title}</SheetTitle>
            </div>
          </SheetHeader>

          {/* Content */}
          <div className="flex-grow overflow-auto p-6">
            {!calendarConnected ? (
              // Calendar Not Connected State
              <div className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                {success && (
                  <Alert>
                    <AlertDescription>{success}</AlertDescription>
                  </Alert>
                )}

                <h2 className="text-lg font-semibold">{d.connectTitle}</h2>
                <p className="text-sm text-muted-foreground">
                  {d.connectDescription}
                </p>

                <div className="flex items-center justify-center gap-3 mt-6">
                  <Button
                    variant="outline"
                    className="gap-2 px-4 py-2 rounded-xl"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleConnectCalendar('google_calendar');
                    }}
                    disabled={loading || connectingProvider !== null || isProviderConnected('google_calendar')}
                  >
                    {connectingProvider === 'google_calendar' ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <GoogleCalendarIcon className="h-5 w-5" />
                    )}
                    <span className="text-sm">
                      {isProviderConnected('google_calendar')
                        ? 'Google Calendar'
                        : connectingProvider === 'google_calendar'
                        ? d.connecting
                        : 'Google Calendar'}
                    </span>
                  </Button>

                  <span className="text-sm text-muted-foreground font-medium">{d.or}</span>

                  <Button
                    variant="outline"
                    className="gap-2 px-4 py-2 rounded-xl"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleConnectCalendar('microsoft_outlook');
                    }}
                    disabled={loading || connectingProvider !== null || isProviderConnected('microsoft_outlook')}
                  >
                    {connectingProvider === 'microsoft_outlook' ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <OutlookCalendarIcon className="h-5 w-5" />
                    )}
                    <span className="text-sm">
                      {isProviderConnected('microsoft_outlook')
                        ? 'Outlook Calendar'
                        : connectingProvider === 'microsoft_outlook'
                        ? d.connecting
                        : 'Outlook Calendar'}
                    </span>
                  </Button>
                </div>

                <Separator className="my-6" />


              </div>
            ) : (
              // Calendar Connected State - Meeting History
              <div className="space-y-6">
                {/* Upcoming Meetings */}
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">
                    {d.upcoming}
                  </h3>
                  {meetingsLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-20 w-full" />
                      ))}
                    </div>
                  ) : upcomingMeetings.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{d.noUpcoming}</p>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {upcomingMeetings.map(meeting => (
                        <MeetingCard key={meeting.id} meeting={meeting} />
                      ))}
                    </div>
                  )}
                </div>

                <Separator />

                {/* Past Meetings */}
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">
                    {d.past}
                  </h3>
                  {meetingsLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-20 w-full" />
                      ))}
                    </div>
                  ) : pastMeetings.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{d.noPast}</p>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {pastMeetings.map(meeting => (
                        <MeetingCard key={meeting.id} meeting={meeting} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer - Settings */}
          {calendarConnected && (
            <div className="p-4 border-t relative z-20">
              <Button
                variant="ghost"
                className="w-full justify-start relative z-20"
                type="button"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  setSettingsModalOpen(true);
                }}
              >
                {d.settings}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
      <MeetingSettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        calendars={calendars}
        meetingAssistantEnabled={meetingAssistantEnabled}
        onMeetingAssistantChange={(enabled) => {
          setMeetingAssistantEnabled(enabled);
          // Refetch meetings to update botScheduled status after toggle
          if (calendars.length > 0) {
            fetchMeetings();
          }
        }}
        onDisconnectSuccess={() => {
          setSettingsModalOpen(false);
          fetchCalendarStatus();
        }}
      />
    </>
  );
};
